import axios from 'axios';
import {
  sanitizeQuery,
  groupKey,
  genericKeyFromRecord,
  aggregateMedicationResults,
  groupsToChoices,
  attachBrandToGroups,
  comboStrengthLabel,
  parseStrength,
  humanizeForm,
  titleCaseName,
} from './medicationNormalize';

const NDC_URL = 'https://api.fda.gov/drug/ndc.json';

function encodeTerm(query) {
  return query.trim().split(/\s+/).map(encodeURIComponent).join('+');
}

function searchClause(query) {
  const q = sanitizeQuery(query);
  if (!q) return '';
  const w = `${encodeTerm(q)}*`;
  return `((generic_name:${w})+OR+(brand_name:${w}))+AND+finished:true`;
}

async function fdaGet(search, extra = '') {
  if (!search) return [];
  const url = `${NDC_URL}?search=${search}${extra}`;
  try {
    const res = await axios.get(url, { timeout: 12000 });
    return res.data?.results || [];
  } catch (err) {
    if (err.response?.status === 404) return [];
    return [];
  }
}

async function lookupBrandGeneric(brand) {
  const term = encodeURIComponent(brand);
  const rows = await fdaGet(`brand_name:"${term}"+AND+finished:true`, '&limit=1');
  return rows[0] || null;
}

/**
 * Search the full OpenFDA NDC directory, then collapse manufacturer/product
 * records into human-friendly medication choices (generic + common brand).
 * Raw NDC records stay available after selection via getMedicationDetails().
 */
export async function searchMedications(query) {
  const q = sanitizeQuery(query);
  if (!q || q.length < 2) return [];

  const clause = searchClause(q);
  const [genericCounts, brandCounts, records] = await Promise.all([
    fdaGet(clause, '&count=generic_name.exact'),
    fdaGet(clause, '&count=brand_name.exact'),
    fdaGet(clause, '&limit=100'),
  ]);

  const { groups, unresolvedBrands } = aggregateMedicationResults({
    query: q,
    genericCounts,
    brandCounts,
    records,
  });

  const toResolve = unresolvedBrands
    .filter((row) => isLikelyRealBrand(row.term))
    .sort((a, b) => (b.count || 0) - (a.count || 0))
    .slice(0, 5);

  if (toResolve.length) {
    const lookups = await Promise.all(
      toResolve.map(async (row) => {
        const rec = await lookupBrandGeneric(row.term);
        return rec ? { row, rec } : null;
      })
    );
    lookups.filter(Boolean).forEach(({ row, rec }) => {
      attachBrandToGroups(groups, row.term, genericKeyFromRecord(rec) || rec.generic_name, row.count || 1);
    });
  }

  const tinyKeys = [...groups.keys()].filter((k) => {
    const g = groups.get(k);
    return g.recordCount <= 3 && !k.includes('/') && !k.includes(' ');
  });
  if (tinyKeys.length) {
    const merges = await Promise.all(tinyKeys.slice(0, 5).map(async (k) => {
      const rec = await lookupBrandGeneric(groups.get(k).name);
      if (!rec) return null;
      const parent = genericKeyFromRecord(rec) || groupKey(rec.generic_name || '');
      if (parent && parent !== k && groups.has(parent)) {
        return { k, rec, parent };
      }
      return null;
    }));
    merges.filter(Boolean).forEach(({ k, rec, parent }) => {
      attachBrandToGroups(groups, groups.get(k).name, rec.generic_name, groups.get(k).recordCount);
      if (parent !== k) groups.delete(k);
    });
  }

  return groupsToChoices(groups, q);
}

function isLikelyRealBrand(term) {
  if (!term) return false;
  const key = groupKey(term);
  if (!key) return false;
  if (key.includes('/')) return false;
  // Skip names that are just the chemical/generic restated
  if (term.split(/\s+/).length > 4) return false;
  return true;
}

/**
 * After the user picks a normalized medication, pull strengths and dosage
 * forms from the underlying NDC records (still the full database).
 */
export async function getMedicationDetails(choice) {
  if (!choice) return emptyDetails();

  const terms = [];
  (choice.genericTerms || []).slice(0, 6).forEach((g) => {
    terms.push(`generic_name:"${encodeURIComponent(g)}"`);
  });
  (choice.brands || []).slice(0, 4).forEach((b) => {
    terms.push(`brand_name:"${encodeURIComponent(b)}"`);
  });
  if (!terms.length && choice.name) {
    terms.push(`generic_name:${encodeTerm(choice.name)}*`);
  }

  const search = `(${terms.join('+OR+')})+AND+finished:true`;
  const records = await fdaGet(search, '&limit=100');
  return detailsFromRecords(choice, records);
}

function emptyDetails() {
  return { strengths: [], forms: [], routes: [], brands: [], recordCount: 0 };
}

function detailsFromRecords(choice, records) {
  const primaryNames = (choice.id || groupKey(choice.name) || '').split('/').filter(Boolean);
  const strengthMap = new Map();
  const formMap = new Map();
  const routeSet = new Set();
  const brandMap = new Map();

  for (const rec of records || []) {
    const recKey = groupKey(rec.generic_name || rec.brand_name || '');
    if (choice.id && recKey && recKey !== choice.id) continue;

    const ingredients = rec.active_ingredients || [];
    const combo = comboStrengthLabel(ingredients, primaryNames);
    if (combo?.label) {
      const prev = strengthMap.get(combo.label);
      strengthMap.set(combo.label, {
        ...combo,
        count: (prev?.count || 0) + 1,
      });
    } else if (ingredients.length === 1) {
      const parsed = parseStrength(ingredients[0].strength);
      if (parsed) {
        const prev = strengthMap.get(parsed.label);
        strengthMap.set(parsed.label, { ...parsed, count: (prev?.count || 0) + 1 });
      }
    }

    if (rec.dosage_form) {
      const form = humanizeForm(rec.dosage_form);
      formMap.set(form, (formMap.get(form) || 0) + 1);
    }
    (rec.route || []).forEach((r) => routeSet.add(titleCaseName(String(r).toLowerCase())));

    const brand = rec.brand_name || rec.brand_name_base;
    if (brand && isDistinctBrandSafe(brand, choice.id)) {
      brandMap.set(titleCaseName(brand), (brandMap.get(titleCaseName(brand)) || 0) + 1);
    }
  }

  const strengths = [...strengthMap.values()]
    .sort((a, b) => a.sortValue - b.sortValue || a.label.localeCompare(b.label));
  const forms = [...formMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);
  const brands = [...brandMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name);

  return {
    strengths,
    forms: forms.length ? forms : (choice.formsPreview || []),
    routes: [...routeSet],
    brands: brands.length ? brands : (choice.brands || []),
    recordCount: records.length,
  };
}

function isDistinctBrandSafe(brand, key) {
  if (!key) return false;
  const bk = groupKey(brand);
  if (!bk || bk === key) return false;
  if (bk.split('/').length !== 1) return false;
  if (key.split('/').includes(bk)) return false;
  return true;
}

export const MEDICATION_FREQUENCIES = [
  'Once daily', 'Twice daily', 'Three times daily', 'Four times daily',
  'Every morning', 'Every evening', 'Every night at bedtime',
  'Every 4 hours', 'Every 6 hours', 'Every 8 hours', 'Every 12 hours',
  'As needed', 'Weekly', 'Bi-weekly', 'Monthly',
];

export const MEDICATION_UNITS = ['mg', 'mcg', 'g', 'ml', 'units', 'tablets', 'capsules', 'drops', 'puffs', 'patches'];

export const MEDICATION_FORMS = [
  'Tablet', 'Tablet (extended release)', 'Capsule', 'Capsule (extended release)',
  'Liquid', 'Liquid (suspension)', 'Injection', 'Inhalation', 'Drops',
  'Cream', 'Ointment', 'Gel', 'Patch', 'Spray', 'Chewable tablet',
];
